import React, { Fragment, ReactElement, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import AddressItem from '../../summary/AddressItem';
import SummaryItem from '../SummaryItem';

// Utils

import { checkIfAddressesEqual } from '@utils/miscellaneous/checkIfAddressesEqual';

// Types

import { Address, IsState } from '@components/ssap/SsapTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';

interface IAddressesInfo {
    addressVisibilityDueToGeneralDelivery: {[key: string]: boolean},
    applicantGuid: string,
    customAddressItemProps?: Array<string> | null,
    hiddenSummaryItems?: Array<string>,
    livingArrangement: string,
    livingArrangementEnabled: boolean,
    homeAddress: Address,
    isState: IsState,
    mailingAddress: Address,
    mailingAddressFromPortal?: Address,
    pcpIndex: number
}

const AddressesInfo = (props: IAddressesInfo): ReactElement => {

    const {
        addressVisibilityDueToGeneralDelivery,
        applicantGuid,
        customAddressItemProps,
        hiddenSummaryItems = [],
        livingArrangement,
        livingArrangementEnabled,
        homeAddress,
        isState,
        mailingAddress,
        mailingAddressFromPortal,
        pcpIndex
    } = props;

    const areMailingAddressesDifferent: boolean = useMemo(
        () => {
            if(!mailingAddressFromPortal) {
                return false;
            }
            return !checkIfAddressesEqual(mailingAddressFromPortal, mailingAddress);
        },
        [ mailingAddressFromPortal, mailingAddress ]
    );

    const getAddressSecondLine = (city, state, postalCode) => {
        return `${ city } ${ state }, ${ postalCode }`;
    };

    const getLivingArrangement = (livingArrangement) => {
        return DROPDOWN_OPTIONS.LIVING_ARRANGEMENT.find(type => type.value === livingArrangement)?.text ?? '';
    };

    const isAddressDiffHidden = hiddenSummaryItems.includes('addressDiff');
    const isHomeAddressHidden = hiddenSummaryItems.includes('homeAddress');
    const isMailingAddressHidden = hiddenSummaryItems.includes('mailingAddress');

    return (
        <Fragment>
            <AddressItem
                customAddressItemProps = { customAddressItemProps }
                isHidden = { isHomeAddressHidden }
                textCode = 'common.home_address'
                { ...homeAddress }
                getAddressSecondLine = { getAddressSecondLine }
                isStreetAddress1Hidden = { addressVisibilityDueToGeneralDelivery[applicantGuid] }
                addressDescriptors = {
                    [
                        [
                            [
                                `taxHousehold.householdMember[${ pcpIndex }].householdContact.homeAddress.streetAddress1`
                            ]
                        ],
                        [
                            [
                                `taxHousehold.householdMember[${ pcpIndex }].householdContact.homeAddress.streetAddress2`
                            ]
                        ],
                        [
                            [
                                [
                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.homeAddress.city`,
                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.homeAddress.state`,
                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.homeAddress.postalCode`
                                ],
                                getAddressSecondLine
                            ]
                        ]
                    ]
                }
            />

            {
                livingArrangementEnabled && <SummaryItem
                    textCode='ssap.input.label.living_arrangement_summary_question'
                    value={getLivingArrangement(livingArrangement)}
                    desc={[
                        [
                            `taxHousehold.householdMember[${pcpIndex}].livingArrangement`,
                            getLivingArrangement
                        ]
                    ]}
                    summaryItemId={'livingArrangement'}
                />
            }

            <AddressItem
                customAddressItemProps = { customAddressItemProps }
                isHidden = { isMailingAddressHidden }
                textCode = 'common.mailing_address'
                { ...mailingAddress }
                getAddressSecondLine = { getAddressSecondLine }
                addressDescriptors = {
                    [
                        [
                            [
                                `taxHousehold.householdMember[${ pcpIndex }].householdContact.mailingAddress.streetAddress1`
                            ]
                        ],
                        [
                            [
                                `taxHousehold.householdMember[${ pcpIndex }].householdContact.mailingAddress.streetAddress2`
                            ]
                        ],
                        [
                            [
                                [
                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.mailingAddress.city`,
                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.mailingAddress.state`,
                                    `taxHousehold.householdMember[${ pcpIndex }].householdContact.mailingAddress.postalCode`
                                ],
                                getAddressSecondLine
                            ]
                        ]
                    ]
                }
            />

            {
                isState.ID && areMailingAddressesDifferent && !isAddressDiffHidden &&

                (
                    <div className = 'usa-alert usa-alert--info gi-alert gi-alert__info gi-alert__print_hidden' >
                        <div className = 'usa-alert__body gi-alert__info__body' >
                            <p className = 'usa-alert__text gi-alert__info__text' >
                                <FormattedMessage id = 'ssap.final_review.mailing_addresses_diff_alert' />
                            </p>
                        </div>
                    </div>
                )
            }
        </Fragment>
    );
};

export default AddressesInfo;